import { useState, useEffect, useMemo, useCallback } from 'react'
import { type ExcelColumnDef, type SortRule, getColKey, getColVal, getColHeader } from './types'

export function useExcelGridState<T>(
  data: T[],
  columns: ExcelColumnDef<T>[],
  id?: string,
  externalQuery?: string,
  userId?: string,
) {
  const storageKey = id ? `mp-wh-grid-${id}` : null
  const widthStorageKey = id ? `erp.grid.${id}.widths.${userId || 'default'}` : null

  // Initial load from localStorage
  const initial = useMemo(() => {
    if (!storageKey) return null
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) return JSON.parse(saved)
    } catch {
      // ignore
    }
    return null
  }, [storageKey])

  const initialWidths = useMemo(() => {
    if (!widthStorageKey) return {}
    try {
      const saved = localStorage.getItem(widthStorageKey)
      if (saved) {
        const parsed = JSON.parse(saved)
        if (parsed && typeof parsed === 'object') return parsed
      }
    } catch {
      // ignore
    }
    return {}
  }, [widthStorageKey])

  const [sort, setSort] = useState<SortRule[]>(() => (Array.isArray(initial?.sort) ? initial.sort : []))
  const [filters, setFilters] = useState<Record<string, string[]>>(() => (initial?.flt && typeof initial.flt === 'object' ? initial.flt : {}))
  const [internalQuery, setInternalQuery] = useState<string>(() => initial?.q || '')
  const [selection, setSelection] = useState<{ a: [number, number]; b: [number, number] } | null>(null)
  const [colWidths, setColWidths] = useState<Record<string, number>>(initialWidths)

  const activeQuery = externalQuery !== undefined ? externalQuery : internalQuery

  // Persist state
  useEffect(() => {
    if (!storageKey) return
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          flt: filters,
          sort,
          q: internalQuery,
        }),
      )
    } catch {
      // ignore
    }
  }, [storageKey, filters, sort, internalQuery])

  // Persist column widths
  useEffect(() => {
    if (!widthStorageKey) return
    try {
      if (Object.keys(colWidths).length === 0) {
        localStorage.removeItem(widthStorageKey)
      } else {
        localStorage.setItem(widthStorageKey, JSON.stringify(colWidths))
      }
    } catch {
      // ignore
    }
  }, [widthStorageKey, colWidths])

  const setColWidth = useCallback((colKey: string, width: number) => {
    setColWidths((prev) => ({
      ...prev,
      [colKey]: Math.max(48, Math.min(640, Math.round(width))),
    }))
  }, [])

  const resetColWidths = useCallback(() => {
    setColWidths({})
    if (widthStorageKey) {
      try {
        localStorage.removeItem(widthStorageKey)
      } catch {
        // ignore
      }
    }
  }, [widthStorageKey])

  const autoFitColumn = useCallback(
    (colKey: string) => {
      const col = columns.find((c) => getColKey(c) === colKey)
      if (!col) return

      const headerText = getColHeader(col)
      let maxPx = 0

      let canvasCtx: CanvasRenderingContext2D | null = null
      try {
        if (typeof document !== 'undefined') {
          const canvas = document.createElement('canvas')
          canvasCtx = canvas.getContext('2d')
          if (canvasCtx) {
            canvasCtx.font = '12.5px Inter, Arial, sans-serif'
          }
        }
      } catch {
        // ignore
      }

      const measure = (text: string) => {
        if (canvasCtx) {
          return canvasCtx.measureText(text).width
        }
        return text.length * 8.5
      }

      maxPx = Math.max(maxPx, measure(headerText) + 36)

      const sampleRows = data.slice(0, 100)
      for (const r of sampleRows) {
        const val = getColVal(col, r)
        const strVal = String(val ?? '')
        if (strVal) {
          maxPx = Math.max(maxPx, measure(strVal) + 24)
        }
      }

      const clamped = Math.max(48, Math.min(420, Math.ceil(maxPx)))
      setColWidths((prev) => ({
        ...prev,
        [colKey]: clamped,
      }))
    },
    [columns, data],
  )

  // Count active filters
  const activeFilterCount = useMemo(() => {
    const colFiltersCount = Object.keys(filters).filter((k) => filters[k] && filters[k].length > 0).length
    return colFiltersCount + (activeQuery ? 1 : 0)
  }, [filters, activeQuery])

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters({})
    setInternalQuery('')
  }, [])

  // Filter & sort data
  const processedData = useMemo(() => {
    let rows = [...data]

    // 1. Full text query filter
    if (activeQuery) {
      const q = activeQuery.toLowerCase().trim()
      rows = rows.filter((r) =>
        columns.some((c) => {
          const val = getColVal(c, r)
          return String(val ?? '').toLowerCase().includes(q)
        }),
      )
    }

    // 2. Column dropdown filters (excluded values)
    for (const key in filters) {
      const excluded = new Set(filters[key])
      if (excluded.size > 0) {
        const col = columns.find((c) => getColKey(c) === key)
        if (col) {
          rows = rows.filter((r) => {
            const val = String(getColVal(col, r) ?? '')
            return !excluded.has(val)
          })
        }
      }
    }

    // 3. Multi-level sort
    const activeSorts = sort
      .map((s) => ({
        col: columns.find((c) => getColKey(c) === s.k),
        dir: s.dir === 'desc' ? -1 : 1,
      }))
      .filter((x): x is { col: ExcelColumnDef<T>; dir: number } => Boolean(x.col))

    if (activeSorts.length > 0) {
      rows.sort((a, b) => {
        for (const { col, dir } of activeSorts) {
          const x = col.sv ? col.sv(a) : getColVal(col, a)
          const y = col.sv ? col.sv(b) : getColVal(col, b)

          if (typeof x === 'number' && typeof y === 'number') {
            const diff = x - y
            if (diff !== 0) return diff * dir
          } else {
            const cmp = String(x ?? '').localeCompare(String(y ?? ''), 'vi', { numeric: true })
            if (cmp !== 0) return cmp * dir
          }
        }
        return 0
      })
    }

    return rows
  }, [data, columns, activeQuery, filters, sort])

  // Bounding box of selection
  const selectionBox = useMemo(() => {
    if (!selection) return null
    return {
      r0: Math.min(selection.a[0], selection.b[0]),
      r1: Math.max(selection.a[0], selection.b[0]),
      c0: Math.min(selection.a[1], selection.b[1]),
      c1: Math.max(selection.a[1], selection.b[1]),
    }
  }, [selection])

  return {
    processedData,
    sort,
    setSort,
    filters,
    setFilters,
    query: internalQuery,
    setQuery: setInternalQuery,
    selection,
    setSelection,
    selectionBox,
    activeFilterCount,
    clearAllFilters,
    colWidths,
    setColWidth,
    resetColWidths,
    autoFitColumn,
  }
}
